import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { DatePicker } from 'ui-components/components/date-picker';
import { Divider } from 'ui-components/components/divider';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService, NOTIFICATION_TYPE_WARNING } from 'ui-components/services/notification-service';

import { useIsUnitsOfMeasureAvailable } from '../../../../../../../feature-gates/use-is-units-of-measure-available';
import { SimpleDropdown } from '../../../../../../../components/simple-dropdown';
import { locationNameFromParts } from '../../../../../../../products/product-details/display-helpers';
import Global from '../../../../../../../store/global-selectors';
import { getInventoryLevelByProductAndWarehouse } from '../../../../../../../store/inventory/inventory.api';
import { ProductGrid } from '../../components/product-grid/product-grid';
import { ProductSearchInput } from '../../components/product-search-input/product-search-input';
import { messages as drawerMessages } from '../../messages';
import { messages } from './messages';
import styles from '../../styles.module.less';

import type { SearchProduct } from '../../components/product-search-input/types';
import type { CreateTransferOrderFormData, FormDataUpdater, Product, ProductLocation, ValidationErrors, Warehouse } from '../../types';
import type { InventoryLevelLocation } from '../../../../types';

const NOTES_MAX_LENGTH = 2000;
const NOTES_PLACEHOLDER = `0/${NOTES_MAX_LENGTH}`;
const PRODUCT_VALIDATION_ERROR_KEYS = ['products', 'quantityExceedsAvailable', 'atLeastOneQuantityRequired'] as const;

type SelectProductsStepProps = {
  formData: CreateTransferOrderFormData;
  onFormDataChange: (updater: FormDataUpdater) => void;
  validationErrors: ValidationErrors;
};

export const SelectProductsStep = ({ formData, onFormDataChange, validationErrors }: SelectProductsStepProps) => {
  const intl = useIntl();
  const inventoryWarehouses = useSelector(Global.selectSellerInventoryWarehouses) as Warehouse[];
  const dateFormat = useSelector(Global.selectUserUserDateFormat) as string;
  const { isAvailable: isUomEnabled } = useIsUnitsOfMeasureAvailable();
  const [warningMessage, setWarningMessage] = useState<string | undefined>(undefined);
  const [infoMessage, setInfoMessage] = useState<string | undefined>(undefined);
  const [referenceNumber, setReferenceNumber] = useState(formData.referenceNumber);
  const [notes, setNotes] = useState(formData.notes);

  const hasProductsWithNoInventory = formData.products.some((product) => product.locations.some((loc) => loc.hasNoInventoryInWarehouse));

  const warehouses = useMemo(
    () =>
      inventoryWarehouses.map((warehouse) => ({
        key: warehouse.inventoryWarehouseId.toString(),
        name: warehouse.name,
      })),
    [inventoryWarehouses],
  );

  const handleInputChange = useCallback(
    (field: keyof CreateTransferOrderFormData, value: string) => {
      onFormDataChange((prev) => ({ ...prev, [field]: value }));
    },
    [onFormDataChange],
  );

  const handleProductSelect = useCallback(
    async (selectedProduct: SearchProduct) => {
      setWarningMessage(undefined);
      setInfoMessage(undefined);

      const existingProduct = formData.products.find((p) => p.productId === selectedProduct.productId);

      if (existingProduct) {
        const targetUomId = isUomEnabled ? (selectedProduct.matchedUomId ?? null) : undefined;
        const hasMatchingRow = existingProduct.locations.some((loc) => loc.unitOfMeasureId === targetUomId);

        if (hasMatchingRow) {
          onFormDataChange((prev) => ({
            ...prev,
            products: prev.products.map((p) => {
              if (p.productId !== selectedProduct.productId) return p;
              let incremented = false;
              return {
                ...p,
                locations: p.locations.map((loc) => {
                  if (incremented || loc.unitOfMeasureId !== targetUomId) return loc;
                  incremented = true;
                  return { ...loc, quantity: loc.quantity + 1 };
                }),
              };
            }),
          }));
          return;
        }

        if (isUomEnabled && selectedProduct.matchedUomId != null) {
          onFormDataChange((prev) => ({
            ...prev,
            products: prev.products.map((p) => {
              if (p.productId !== selectedProduct.productId) return p;
              return {
                ...p,
                locations: p.locations.map((loc) => ({ ...loc, unitOfMeasureId: selectedProduct.matchedUomId, quantity: 0 })),
              };
            }),
          }));
        }

        setInfoMessage(intl.formatMessage(messages.productAlreadyAdded, { productName: selectedProduct.name }));
        return;
      }

      if (formData.originWarehouseId) {
        try {
          const inventoryData: InventoryLevelLocation[] =
            (await getInventoryLevelByProductAndWarehouse(selectedProduct.productId, formData.originWarehouseId).promise) ?? [];

          if (inventoryData.length > 0) {
            const locations = inventoryData
              .filter((loc) => loc.inventoryLocationId)
              .map((loc): ProductLocation => {
                const lotPart = loc.lotUuid ? `-${loc.lotUuid}` : '';
                const rowId = `${selectedProduct.productId}-${loc.inventoryLocationId}${lotPart}`;

                return {
                  rowId,
                  locationId: loc.inventoryLocationId.toString(),
                  location: locationNameFromParts(loc),
                  onHand: loc.stock ?? 0,
                  available: loc.available ?? 0,
                  quantity: 0,
                  lotId: loc.lot,
                  lotUuid: loc.lotUuid,
                  expirationDate: loc.expirationDate,
                };
              });

            const unitsOfMeasure = isUomEnabled ? (selectedProduct.unitsOfMeasure ?? []) : undefined;

            const newProduct: Product = {
              productId: selectedProduct.productId,
              name: selectedProduct.name,
              sku: selectedProduct.sku,
              thumbnailUrl: selectedProduct.thumbnailUrl,
              unitsOfMeasure,
              locations: locations.map((loc) => ({
                ...loc,
                unitOfMeasureId: isUomEnabled ? (selectedProduct.matchedUomId ?? null) : undefined,
              })),
            };

            onFormDataChange((prev) => ({ ...prev, products: [newProduct, ...prev.products] }));
            return;
          }
        } catch {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_WARNING,
            title: intl.formatMessage(drawerMessages.inventoryFetchErrorTitle),
            content: intl.formatMessage(messages.inventoryFetchErrorContent, { productName: selectedProduct.name }),
          });
          return;
        }
      }
      setWarningMessage(intl.formatMessage(messages.noInventoryWarning, { productName: selectedProduct.name }));
    },
    [formData.products, formData.originWarehouseId, intl, isUomEnabled, onFormDataChange],
  );

  const isSearchDisabled = !formData.originWarehouseId;

  return (
    <div className={styles['content-container']}>
      <div className={styles['section-header']}>
        <Text textType={TextType.H4} value={messages.selectProductsStepHeader} />
      </div>
      <div className={styles.section}>
        <FlexRow gap={Gap.Space24}>
          <FlexColumn className={styles['warehouse-dropdown']} gap={Gap.Space4}>
            <FlexRow gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.originWarehouseLabel} />
              <span className={styles['required-indicator']}>*</span>
            </FlexRow>
            <SimpleDropdown
              defaultTitle={intl.formatMessage(messages.selectWarehouse)}
              dropdownMenuShouldHaveWidthOfButton
              fullWidth
              hasError={!!validationErrors.originWarehouseId}
              items={warehouses}
              onItemSelected={(value: string) => handleInputChange('originWarehouseId', value)}
              selectedItemKey={formData.originWarehouseId}
            />
            {validationErrors.originWarehouseId ? (
              <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                <Text textType={TextType.Caption} value={validationErrors.originWarehouseId.message} />
              </InlineMessage>
            ) : null}
            <Text className={styles['warehouse-hint']} textType={TextType.Caption} value={messages.originWarehouseHint} />
          </FlexColumn>

          <FlexColumn className={styles['warehouse-dropdown']} gap={Gap.Space4}>
            <FlexRow gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.destinationWarehouseLabel} />
              <span className={styles['required-indicator']}>*</span>
            </FlexRow>
            <SimpleDropdown
              defaultTitle={intl.formatMessage(messages.selectWarehouse)}
              dropdownMenuShouldHaveWidthOfButton
              fullWidth
              hasError={!!validationErrors.destinationWarehouseId}
              items={warehouses}
              onItemSelected={(value: string) => handleInputChange('destinationWarehouseId', value)}
              selectedItemKey={formData.destinationWarehouseId}
            />
            {validationErrors.destinationWarehouseId ? (
              <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                <Text textType={TextType.Caption} value={validationErrors.destinationWarehouseId.message} />
              </InlineMessage>
            ) : null}
            <Text className={styles['warehouse-hint']} textType={TextType.Caption} value={messages.destinationWarehouseHint} />
          </FlexColumn>
        </FlexRow>

        <FlexRow className={styles['additional-fields-container']} gap={Gap.Space24}>
          <FlexColumn className={styles['additional-fields-left']} gap={Gap.Space16}>
            <FlexRow gap={Gap.Space24}>
              <FlexColumn className={styles['field-container']} gap={Gap.Space4}>
                <Text textType={TextType.Label} value={drawerMessages.orderDateLabel} />
                <DatePicker
                  allowPastDates
                  calendarPosition='bottom-right'
                  className={styles['date-picker']}
                  dateFormat={dateFormat}
                  onChange={(value: string) => handleInputChange('orderDate', value)}
                  placeholder=''
                  shouldInputHaveFullWidth
                  value={formData.orderDate}
                />
              </FlexColumn>

              <FlexColumn className={styles['field-container']} gap={Gap.Space4}>
                <Text textType={TextType.Label} value={drawerMessages.expectedDeliveryDateLabel} />
                <DatePicker
                  allowPastDates
                  calendarPosition='bottom-right'
                  className={styles['date-picker']}
                  dateFormat={dateFormat}
                  onChange={(value: string) => handleInputChange('expectedDeliveryDate', value)}
                  placeholder=''
                  shouldInputHaveFullWidth
                  value={formData.expectedDeliveryDate}
                />
              </FlexColumn>
            </FlexRow>

            <FlexRow gap={Gap.Space24}>
              <FlexColumn className={styles['field-container']} gap={Gap.Space4}>
                <Text textType={TextType.Label} value={drawerMessages.referenceNumberLabel} />
                <Input
                  name='referenceNumber'
                  onBlur={() => handleInputChange('referenceNumber', referenceNumber)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReferenceNumber(e.target.value)}
                  value={referenceNumber}
                  version={version2}
                />
              </FlexColumn>
            </FlexRow>
          </FlexColumn>

          <FlexColumn className={styles['field-container']} gap={Gap.Space4}>
            <Text textType={TextType.Label} value={drawerMessages.notesLabel} />
            <div className={styles['notes-input-wrapper']}>
              <FloatTextArea
                maxLength={NOTES_MAX_LENGTH}
                onBlur={() => handleInputChange('notes', notes)}
                onChange={(value: string) => setNotes(value)}
                placeholder={NOTES_PLACEHOLDER}
                value={notes}
              />
            </div>
          </FlexColumn>
        </FlexRow>
      </div>

      <Divider className={styles['horizontal-divider']} />

      <div className={styles.section}>
        <div className={styles['product-search-section']}>
          <Text textType={TextType.Label} value={messages.addProductsLabel} />
          <ProductSearchInput
            disabled={isSearchDisabled}
            infoMessage={infoMessage}
            isUomEnabled={isUomEnabled}
            onProductSelect={handleProductSelect}
            showAddButton
            warningMessage={warningMessage}
          />
        </div>
        {PRODUCT_VALIDATION_ERROR_KEYS.map(
          (key) =>
            validationErrors[key] && (
              <InlineMessage key={key} type={INLINE_MESSAGE_TYPES.error}>
                <Text textType={TextType.Caption} value={validationErrors[key].message} />
              </InlineMessage>
            ),
        )}
        {hasProductsWithNoInventory ? (
          <InlineMessage type={INLINE_MESSAGE_TYPES.warning}>
            <Text textType={TextType.Caption} value={messages.someProductsNoInventoryWarning} />
          </InlineMessage>
        ) : null}
        <ProductGrid products={formData.products} setFormData={onFormDataChange} />
      </div>
    </div>
  );
};
